import { ChevronLeft, X } from "lucide-react";
import { useState } from "react";
import * as RPNInput from "react-phone-number-input";
import { Button } from "@workspace/web-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@workspace/web-ui/components/dialog";
import { Input } from "@workspace/web-ui/components/input";
import { Label } from "@workspace/web-ui/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@workspace/web-ui/components/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { usePayForRefillMedicationMutation } from "../pages/medicals/api/mutations/medications";
import { getPrescriptionMedicationByMedicationIdQueryOptions } from "../pages/medicals/api/queries";
import { deliveryAddressSchema, MedicationPaymentAddress } from "../pages/medicals/schemas";
import { states } from "~/data";
import { useAuth } from "~/providers/auth.provider";
import { CountrySelect, FlagComponent, PhoneInput } from "~/components/phone-input";

export const MedicationRefillPaymentModal = ({
  open,
  onClose,
  prescription_id,
  medication_id,
  program = 'weight_loss'
}: {
  open: boolean;
  onClose: () => void;
  prescription_id: string;
  medication_id: string;
  program?: 'weight_loss' | 'urgent_care';
}) => {
  const [step, setStep] = useState(1);
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);
  const { user } = useAuth()

  const form = useForm<MedicationPaymentAddress>({
    resolver: zodResolver(deliveryAddressSchema),
    defaultValues: {
      street: user?.delivery_address?.street ? user?.delivery_address?.street : "",
      city: user?.delivery_address?.city ? user?.delivery_address?.city : "",
      state: user?.delivery_address?.state ? user?.delivery_address?.state : "",
      zip_code: user?.delivery_address?.zip_code ? user?.delivery_address?.zip_code : "",
    }
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = form;

  const { mutate: createPaymentFoRefillMedication, isPending: paymentLoading } = usePayForRefillMedicationMutation()

  const { data: prescriptionData, isLoading: prescriptionLoading } = useQuery(getPrescriptionMedicationByMedicationIdQueryOptions(prescription_id, medication_id))

  const medicationData = prescriptionData?.medication

  const refillMedicationSuccessUrl = `${window.location.origin}/dashboard/weight-loss/medicals/refill-confirmation`;
  const refillMedicationErrorUrl = `${window.location.origin}/dashboard/weight-loss/medicals/error`;

  const handlePaymentSubmit = (data: MedicationPaymentAddress) => {
    createPaymentFoRefillMedication({
      prescription_id: prescription_id,
      medication_id: medication_id,
      delivery_address: {
        street: data.street ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        phone_number: data.phone_number ?? "",
        zip_code: data.zip_code ?? "",
      },
      program: program,
      success_url: refillMedicationSuccessUrl,
      cancel_url: refillMedicationErrorUrl,
    },
      {
        onSuccess: (response) => {
          if (response.success) {
            window.location.replace(response?.data?.payment_metadata?.url);
          }
        }
      });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <div
        className={` ${open ? "fixed inset-0 z-40 h-screen bg-black/5 backdrop-blur-sm" : ""} `}
      />
      <DialogContent className="max-w-md rounded-lg p-6 sm:max-w-lg [&>button]:hidden">
        <DialogHeader className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center w-full">
            <p className="text-sm font-medium">Step {step}/3</p>
            <Button
              onClick={onClose}
              className="absolute right-4 top-4 size-8 rounded-full bg-[#0404040D] text-gray-400 hover:bg-[#0404040D] hover:text-gray-600"
            >
              <X className="h-6 w-6 text-[#363840]" />
            </Button>
          </div>
          <div className="">
            <DialogTitle className="mt-6 text-3xl font-medium">
              Pay for {program === 'weight_loss' ? 'Weight loss' : 'Urgent care'} medication
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* STEP 1: Select Medications */}
        {step === 1 && (
          <div>
            <div className="space-y-14 max-h-[300px] overflow-y-auto">
              <div className="bg-[#F1F1F1] rounded-lg px-5 py-4 mx-auto space-y-3">
                {prescriptionLoading ? (
                  <>
                    {[1, 2, 3].map((index) => (
                      <div key={index} className="flex items-center justify-between animate-pulse">
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col">
                            <div className="flex items-start">
                              <div className="flex flex-col space-y-2">
                                <div className="h-4 w-48 bg-gray-300 rounded" />
                                <div className="h-4 w-24 bg-gray-300 rounded" />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="h-4 w-16 bg-gray-300 rounded" />
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="flex items-start justify-between ">
                    <div className="flex items-center gap-2">
                      <div className="flex flex-col">
                        <div className="flex items-start">
                          <div className="flex flex-col space-y-2">
                            <Label htmlFor={`med-${medicationData?.medication_id}`} className="text-sm">
                              {medicationData?.medication.name} {medicationData?.medication.duration ? `(${medicationData?.medication.duration.includes("days") ? medicationData?.medication.duration : "days"} supply)` : null}
                            </Label>
                            <span className="inline-flex items-center">
                              {" "}
                              {medicationData?.medication.quantity ? (
                                <h6 className="text-sm text-[#202020CC]">
                                  Quantity: {medicationData?.medication.quantity}{" "}
                                </h6>
                              ) : null}
                            </span>
                          </div>
                        </div>
                        <div className="flex">
                          <h6 className="text-xs sm:text-sm font-normal text-[#464B55] text-left">
                            Quantity: {medicationData?.medication.quantity} <span className="mx-1 text-[#464B55]">{"\u2022"}</span>
                            {medicationData?.medication.dosage} <span className="mx-1 text-[#464B55]">{"\u2022"}</span> {medicationData?.medication.duration.includes("days") ? medicationData?.medication.duration : "days"}
                          </h6>
                        </div>
                      </div>
                    </div>
                    {medicationData?.medication.cost ? <span className="text-sm font-medium">$ {medicationData?.medication.cost}</span> : null}

                  </div>
                )}
              </div>

              <div className="pt-5 space-y-6">
                <div className="flex items-center justify-between w-full">
                  <p className="text-sm">Total:</p>
                  <p className="text-sm font-semibold">$ {medicationData?.medication.cost}</p>
                </div>

                <Button
                  className="w-full text-base font-normal h-11 bg-[#4272DD] rounded-lg"
                  disabled={prescriptionLoading}
                  onClick={nextStep}
                >
                  Continue
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Address Form */}
        {step === 2 && (
          <div className="space-y-4">
            <h1 className="text-xl font-medium">Confirm shipping address</h1>
            <form
              onSubmit={handleSubmit(handlePaymentSubmit)}
              id="medication-for-payment-form"
              className="space-y-4"
            >
              <div className="space-y-3">
                <Label htmlFor="street" className="text-sm">
                  Address
                </Label>
                <Input
                  id="street"
                  {...register("street")}
                  aria-invalid={!!errors.street}
                  placeholder="Enter your address"
                  className="h-10 px-3 py-2"
                />
                {errors.street && (
                  <span className="text-sm text-red-500">Street Address is Required</span>
                )}
              </div>

              <div className="flex gap-2">
                <div className="space-y-2 w-full">
                  <Label htmlFor="state" className="text-sm">
                    State
                  </Label>
                  <div className="flex gap-2">
                    <Select onValueChange={(value) => setValue("state", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select State" />
                      </SelectTrigger>
                      <SelectContent id="state"
                      >
                        {states.map((state) => (
                          <SelectItem key={state.value} value={state.value}>{state.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 w-full">
                  <Label className="text-sm">State/ZIP</Label>
                  <Input id="zip_code"  {...register("zip_code")}
                    className="h-10 px-3 py-2"
                    aria-invalid={!!errors.zip_code} placeholder="ZIP Code" />
                </div>
              </div>

              <div className="w-full">
                <Label htmlFor="phone_number">Phone number</Label>
                <Controller
                  control={control}
                  name="phone_number"
                  render={({ field }) => (
                    <RPNInput.default
                      className="flex rounded-md w-full shadow-xs"
                      international
                      flagComponent={FlagComponent}
                      countrySelectComponent={CountrySelect}
                      defaultCountry="US"
                      inputComponent={PhoneInput}
                      id="phone_number"
                      placeholder="Enter phone number"
                      value={field.value}
                      onChange={(newValue) => field.onChange(newValue ?? "")}
                    />
                  )}
                />

              </div>

              <div className="flex justify-end gap-3 pt-5 text-base font-normal">
                <Button variant="outline" className="rounded-lg h-11" onClick={prevStep}>
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                <Button type="submit" form="medication-for-payment-form" className="text-base font-normal w-full h-11 bg-[#4272DD] rounded-lg">{paymentLoading ? "Checkout..." : "Continue"}</Button>
              </div>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
